import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, UserPlus, AlertCircle, Shield, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { hardRefresh } from '../offline/offlineManager';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Show session expired message
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setError('Votre session a expiré. Veuillez vous reconnecter.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(email, password, rememberMe);
      } else {
        // Validate password for registration
        if (password.length < 12) {
          setError('Le mot de passe doit contenir au moins 12 caractères');
          setLoading(false);
          return;
        }
        await register(email, password, nom);
      }
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYXJjaGl0ZWN0JTIwYmx1ZXByaW50fGVufDB8fHx8MTc2OTc5NzczM3ww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img 
            src="https://customer-assets.emergentagent.com/job_parlez-amis/artifacts/ief9lfq6_image.png" 
            alt="MAAG Logo" 
            className="w-32 h-32 mb-6 object-contain"
          />
          <h1 className="text-4xl font-bold mb-4 text-center">MAAG</h1>
          <p className="text-xl text-slate-300 text-center max-w-md">
            Maîtrise d'œuvre d'espaces commerciaux
          </p>
          <p className="text-sm text-slate-400 mt-4 text-center">
            Mathieu GOND - Fondateur
          </p>
          
          {/* Security badge */}
          <div className="mt-8 flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-xs text-slate-300">Connexion sécurisée</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_parlez-amis/artifacts/ief9lfq6_image.png" 
              alt="MAAG Logo" 
              className="w-10 h-10 object-contain mr-3"
            />
            <span className="text-2xl font-bold text-slate-900">MAAG</span>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {isLogin ? 'Connexion' : 'Créer un compte'}
            </h2>
            <p className="text-slate-500 mb-6">
              {isLogin 
                ? 'Accédez à votre espace de gestion de devis' 
                : 'Commencez à gérer vos devis professionnels'}
            </p>

            {error && (
              <div className={`mb-4 p-3 rounded-md flex items-start text-sm ${
                error.includes('expiré') 
                  ? 'bg-amber-50 border border-amber-200 text-amber-700'
                  : error.includes('verrouillé') || error.includes('tentative')
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
              }`} data-testid="error-message">
                {error.includes('verrouillé') ? (
                  <Lock className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nom complet
                  </label>
                  <input
                    data-testid="input-nom"
                    type="text"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="Votre nom"
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  placeholder="votre@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    data-testid="input-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-10 px-3 pr-10 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="••••••••••••"
                    required
                    minLength={isLogin ? undefined : 12}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-slate-500 mt-1">
                    Minimum 12 caractères
                  </p>
                )}
              </div>

              {isLogin && (
                <div className="flex items-center">
                  <input
                    data-testid="remember-me-checkbox"
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900 cursor-pointer"
                  />
                  <label htmlFor="remember-me" className="ml-2 text-sm text-slate-600 cursor-pointer">
                    Se souvenir de moi (30 jours)
                  </label>
                </div>
              )}

              <button
                data-testid="submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : isLogin ? (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Se connecter
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Créer le compte
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                data-testid="toggle-mode-btn"
                onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                {isLogin 
                  ? "Pas encore de compte ? Créer un compte" 
                  : "Déjà un compte ? Se connecter"}
              </button>
            </div>
          </div>
          
          {/* Hard refresh (équivalent Ctrl+Shift+R : vide le cache et recharge la dernière version) */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => hardRefresh()}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 bg-white transition-colors"
              title="Vide le cache de l'application et recharge la dernière version (comme Ctrl+Shift+R)"
              data-testid="hard-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Recharger la dernière version
            </button>
          </div>

          {/* Security info */}
          <div className="mt-4 text-center text-xs text-slate-400">
            <div className="flex items-center justify-center gap-1">
              <Lock className="w-3 h-3" />
              <span>Connexion sécurisée • Session {rememberMe ? 'de 30 jours' : 'de 60 min'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
